import type { Root } from "./prototypeTypes";
import { launchFireworks } from "./celebration";
import { markCompletedVideoChapter, runtimeOf } from "./mediaPlayerProgress";
import { playerSelectors } from "./mediaPlayerSelectors";

/**
 * Final-chapter burst + replay card.
 *
 * Two entry points share one idempotent final presenter:
 *   - `presentFinalVideoChapter` is invoked after native `ended` confirms
 *     that the mounted video is the final chapter.
 *   - `maybeFinalChapterBurst` is invoked from the `timeupdate` loop
 *     and pre-rolls the celebration on the LAST chapter as soon as
 *     the visual end-of-content is reached (~3s of buffer remain)
 *     so the fireworks do not have to wait for the actual `ended`
 *     event. The <video> is NOT paused here — it keeps playing
 *     out naturally. The shared presenter owns the replay card and
 *     is idempotent: only the first call per chapter
 *     per playback session fires the burst.
 *
 * The burst is anchored to `#videoFullscreen` (the replay card's
 * parent) so the confetti stays inside the player viewport.
 */
let lastBurstKey: string | null = null;

export function recordCompletedVideoChapter(root: Root, topicId: string, chapterIndex: number): void {
  markCompletedVideoChapter(root, `${topicId}:${chapterIndex}`);
}

export function presentFinalVideoChapter(root: Root, topicId: string, chapterIndex: number): void {
  const key = `${topicId}:${chapterIndex}`;
  if (lastBurstKey === key) return;
  lastBurstKey = key;
  showFinalChapterCelebration(root);
}

export function maybeFinalChapterBurst(
  root: Root,
  media: { currentTime: number; duration: number; pause?: () => void },
): void {
  const rt = runtimeOf(root);
  const total = (root as unknown as { __videoChapterCount?: number }).__videoChapterCount ?? 0;
  if (!rt?.activeChapterIndex || total <= 0) return;
  if (rt.activeChapterIndex < total) return; // not the last chapter
  if (!Number.isFinite(media.duration) || media.duration <= 0) return;
  // Spec: the last chapter must NOT play all the way to the end —
  // we stop ~3s early, pause playback, and fire the full-screen
  // fireworks finale so the topic ends on the celebration rather
  // than empty silence/black frames.
  const remaining = media.duration - media.currentTime;
  if (remaining > 3) return;
  if (!rt.activeCourseId) return;
  recordCompletedVideoChapter(root, rt.activeCourseId, rt.activeChapterIndex);
  // Fire the celebration the moment the final chapter reaches its
  // visual end-of-content (3s of buffer remain) but DO NOT pause the
  // <video> here. Pausing earlier produced a jarring freeze: the
  // progress bar stopped 2-3s before the chapter actually finished
  // and the replay card snapped in while audio was still audible.
  // Letting playback finish naturally keeps the progress bar moving to
  // 100%; the later ended path calls the same idempotent presenter.
  presentFinalVideoChapter(root, rt.activeCourseId, rt.activeChapterIndex);
}

export function resetFinalChapterBurst(): void {
  lastBurstKey = null;
}

function showFinalChapterCelebration(root: Root): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const vf = doc.getElementById(playerSelectors.video.container);
  const replay = doc.getElementById(playerSelectors.video.replay);
  if (replay) replay.classList.add("active");
  if (vf) vf.classList.add("replay-active", "controls-visible");
  // Full-screen, screen-centered fireworks. Anchor to the fullscreen
  // player when it is active so the show overlays the video; fall
  // back to document.body otherwise.
  launchFireworks({
    durationMs: 6000,
    shells: 8,
    message: "Topic complete",
    host: vf?.classList.contains("active") ? vf : null,
  });
}
